import * as Dfns from 'date-fns/fp';
import { getColorMap, shadeColor } from '@src/utils/colors';
import { formatPercentRounded } from '../../utils/hroasUtils';
import { SIBLING_SHADE_STEPS } from '../../utils/modelOverviewUtils';
import { ModelFamily, Slot, tagRowsWithSlot } from '../../utils/slots';
import { MarketingOutcomesProcessedRow } from '../../utils/useEfficientFrontierData';
import { BrandEquityColor, GeoMMMColor, RMMMColor } from './colors';
import { ChannelMixDataPoint, PinnedSpendPointData, TransformedSpendData } from '../../hroas-types';

/** Whole-day span of a `[start, end]` ISO date range, used to detect whether a new Reference
 * Range selection preserves the same range length (DEV-24477) as the previous one. */
export const getDateRangeLengthDays = (start: string, end: string): number =>
  Dfns.differenceInDays(new Date(start), new Date(end));

export const getChannelGroupName = (channel: string): string => {
  const parts = channel.split(',');
  if (parts.length === 0) {
    return channel;
  }

  const firstPart = parts[0];
  return firstPart
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getChannelGroups = (data: any[]): string[] => {
  const groups = new Set<string>();
  data.forEach(budgetData => {
    budgetData.channels.forEach(channel => {
      groups.add(getChannelGroupName(channel.channel));
    });
  });
  return Array.from(groups).sort();
};

export const getChannelMixData = (data: any[], isRelative: boolean): ChannelMixDataPoint[] => {
  const channelGroups = getChannelGroups(data);

  return data.map(budgetData => {
    const dataPoint: ChannelMixDataPoint = {
      totalBudget: budgetData.totalBudget
    };

    channelGroups.forEach(group => {
      dataPoint[group] = 0;
    });

    let totalChannelSpend = 0;
    budgetData.channels.forEach(channel => {
      const groupName = getChannelGroupName(channel.channel);
      const totalSpend = channel.featureSpend;
      dataPoint[groupName] = (dataPoint[groupName] as number) + totalSpend;
      totalChannelSpend += totalSpend;
    });

    if (isRelative && totalChannelSpend > 0) {
      channelGroups.forEach(group => {
        const spend = dataPoint[group] as number;
        dataPoint[group] = (spend / totalChannelSpend) * 100;
      });
    }

    return dataPoint;
  });
};

export const createChannelColorMap = (channelGroups: string[]): Record<string, string> => {
  const channelGroupObjects = channelGroups.map(name => ({ name }));
  return getColorMap(channelGroupObjects, undefined, undefined, true);
};

interface IndicatorResult {
  indicatorClassName: string;
  indicatorText: string;
  percentChange: number | null;
}

export const getPercentageDifferenceIndicator = (
  rowValue: number,
  bauValue: number,
  isBau: boolean,
  showBauLabel = false
): IndicatorResult => {
  if (isBau) {
    return showBauLabel
      ? {
          indicatorClassName: 'ef-bau-indicator',
          indicatorText: 'BAU',
          percentChange: null
        }
      : { indicatorClassName: '', indicatorText: '', percentChange: null };
  }

  if (bauValue === 0) {
    return { indicatorClassName: '', indicatorText: '', percentChange: null };
  }
  if (rowValue > bauValue) {
    const percentageDifference = (rowValue - bauValue) / bauValue;
    const percentChange = Math.round(percentageDifference * 100);
    return {
      indicatorClassName: 'ef-above-bau-indicator',
      indicatorText: `+${formatPercentRounded(percentageDifference * 100)}`,
      percentChange
    };
  }
  if (rowValue < bauValue) {
    const percentageDifference = (rowValue - bauValue) / bauValue;
    const percentChange = Math.round(percentageDifference * 100);
    return {
      indicatorClassName: 'ef-below-bau-indicator',
      indicatorText: formatPercentRounded(percentageDifference * 100),
      percentChange
    };
  }
  return { indicatorClassName: 'ef-bau-indicator', indicatorText: '+0%', percentChange: 0 };
};

/**
 * Recomputes a data point's total-holistic revenue/ROAS against the currently selected slot set,
 * dropping the revenue of any toggled-off slot. This is the single source used both to filter every
 * row shown on the Marketing Outcomes chart and to derive `businessAsUsualFiltered` in
 * EfficientFrontier.tsx -- keeping the chart, its hover tooltip, and the Pinned Spend Points table
 * from ever disagreeing on what BAU's revenue is under the current toggle state.
 */
export const applySelectedModelsFilter = (
  dataPoint: PinnedSpendPointData,
  selectedModels: Set<string>
): PinnedSpendPointData => {
  const revenueBySlot: Record<string, number> = {};
  Object.entries(dataPoint.revenueBySlot ?? {}).forEach(([slotId, revenue]) => {
    if (selectedModels.has(slotId)) {
      revenueBySlot[slotId] = revenue;
    }
  });
  const totalRevenue = Object.values(revenueBySlot).reduce((sum, revenue) => sum + revenue, 0);
  return {
    ...dataPoint,
    revenueBySlot,
    totalHolisticRevenue: totalRevenue,
    totalHolisticRoas: dataPoint.totalBudget > 0 ? totalRevenue / dataPoint.totalBudget : 0
  };
};

/**
 * Groups the per-(budget, model_type, model_scope) rows into one chart point per budget with the
 * revenue of each slot; the total is the plain sum of attributed revenue across slots.
 */
export const buildMarketingOutcomePoints = (
  rows: MarketingOutcomesProcessedRow[],
  slots: Slot[]
): PinnedSpendPointData[] => {
  const tagged = tagRowsWithSlot(
    rows.map(row => ({ ...row, model_type: row.modelType, model_scope: row.modelScope })),
    slots
  );
  const pointsByBudget: Record<number, PinnedSpendPointData> = {};
  tagged.forEach(row => {
    pointsByBudget[row.totalBudget] ??= {
      totalBudget: row.totalBudget,
      isBau: row.isBau,
      revenueBySlot: {},
      totalHolisticRevenue: 0,
      totalHolisticRoas: 0
    };
    const point = pointsByBudget[row.totalBudget];
    (point.revenueBySlot ??= {})[row.slotId] = row.modelRevenue;
    point.totalHolisticRevenue = (point.totalHolisticRevenue ?? 0) + (row.modelRevenue || 0);
    point.totalHolisticRoas =
      row.totalBudget > 0 ? point.totalHolisticRevenue / row.totalBudget : 0;
  });
  return Object.values(pointsByBudget).sort((a, b) => a.totalBudget - b.totalBudget);
};

export interface EfficientFrontierSeries {
  id: string;
  label: string;
  family: ModelFamily;
  color: string;
}

const FAMILY_BASE_COLOR: Record<ModelFamily, string> = {
  rmmm: RMMMColor,
  geommm: GeoMMMColor,
  brand_equity: BrandEquityColor
};

const STACK_FAMILY_ORDER: ModelFamily[] = ['rmmm', 'geommm', 'brand_equity'];
const DISPLAY_FAMILY_ORDER: ModelFamily[] = ['geommm', 'rmmm', 'brand_equity'];

/**
 * One series per enabled slot. The first slot of a family keeps the family's Efficient Frontier
 * color; further slots of that family get shaded variants. Colors are assigned over ALL slots so a
 * toggle never recolors the others.
 */
export const buildEfficientFrontierSeries = (
  slots: Slot[],
  models: { id: string; appLabel: string; enabled: boolean }[],
  order: 'stack' | 'display'
): EfficientFrontierSeries[] => {
  const familyOrder = order === 'stack' ? STACK_FAMILY_ORDER : DISPLAY_FAMILY_ORDER;
  const seenInFamily: Partial<Record<ModelFamily, number>> = {};
  return slots
    .map(slot => {
      const siblingIndex = seenInFamily[slot.family] ?? 0;
      seenInFamily[slot.family] = siblingIndex + 1;
      const base = FAMILY_BASE_COLOR[slot.family];
      return {
        id: slot.slotId,
        label: models.find(model => model.id === slot.slotId)?.appLabel ?? slot.label,
        family: slot.family,
        color:
          siblingIndex === 0
            ? base
            : shadeColor(base, SIBLING_SHADE_STEPS[(siblingIndex - 1) % SIBLING_SHADE_STEPS.length])
      };
    })
    .filter(series => models.some(model => model.id === series.id && model.enabled))
    .sort((a, b) => familyOrder.indexOf(a.family) - familyOrder.indexOf(b.family));
};

// --- DEV-21410: dynamic (date-range-driven) Business As Usual ---
//
// The backend routine's `isBau`/`bau` flag marks ONE fixed budget level (its own average-weekly-
// spend anchor) that never moves with the page's selected date range. Per DEV-21410 (Philip
// Rundall, comment 1390975), BAU should instead be computed from the user's currently-selected
// date range: sum actual spend across all channels/weeks in range, then linearly interpolate
// between the two real optimized budget levels the EF already emits (marketingOutcomesData's
// `totalBudget` values / the channel-mix budget map's `totalBudget` values) that bracket it. The
// routine's own flag becomes informational only ("modeled anchor"), not the source of the
// displayed BAU point. Away only -- Poppi was dropped from scope per direct conversation with Phil.
//
// A note on units: marketingOutcomesData's `totalBudget` (and the channel-mix budget map's,
// reached via the same budgetKeyMapping) are already summed over every week in the selected range
// (see useEfficientFrontierData.ts's processMarketingOutcomes), i.e. they're period totals, not
// single-week amounts. The blend factor `t` computed below is scale-invariant (multiplying the
// bracket target and both endpoints by the same constant leaves `t` unchanged), so bracketing the
// undivided period-total spend against these period-total budget levels gives the identical
// interpolated point as bracketing a true weekly average against weekly-basis levels would. We
// still report `bauWeeklySpend` (divided by the number of weeks) since that's the natural unit for
// comparing against the routine's own per-week budget-level scenarios.

/** Sum of `raw_spend` for spend rows whose `date` falls within `[start, end]` (inclusive). */
export const sumActualSpendInDateRange = (
  spendRows: TransformedSpendData[],
  start: string,
  end: string
): number =>
  spendRows
    .filter(row => Boolean(row.date) && row.date >= start && row.date <= end)
    .reduce((sum, row) => sum + (Number(row.raw_spend) || 0), 0);

/** `sumActualSpendInDateRange` divided down to a weekly average, for display/reference only. */
export const computeBauWeeklySpend = (
  spendRows: TransformedSpendData[],
  start: string,
  end: string,
  numberOfWeeks: number
): number =>
  numberOfWeeks > 0 ? sumActualSpendInDateRange(spendRows, start, end) / numberOfWeeks : 0;

export interface BudgetBracket<T> {
  lo: T;
  hi: T;
  /** Blend factor in [0, 1]; 0 at `lo`, 1 at `hi`. */
  t: number;
  isClamped: boolean;
  clampedDirection: 'min' | 'max' | null;
}

/**
 * Finds the pair of adjacent budget levels (sorted ascending by `totalBudget`) that bracket
 * `targetBudget`, and the blend factor between them. When `targetBudget` lands exactly on a level,
 * `lo`/`hi` are that same level and `t` is 0. When `targetBudget` falls outside `[min, max]`, this
 * clamps to the nearest endpoint (`lo === hi === that endpoint`, `t` is 0) and flags the direction
 * -- callers must never extrapolate past the modeled range.
 */
export const findBudgetBracket = <T extends { totalBudget: number }>(
  sortedLevels: T[],
  targetBudget: number
): BudgetBracket<T> | undefined => {
  if (sortedLevels.length === 0) {
    return undefined;
  }

  const min = sortedLevels[0];
  const max = sortedLevels[sortedLevels.length - 1];

  if (targetBudget <= min.totalBudget) {
    return {
      lo: min,
      hi: min,
      t: 0,
      isClamped: targetBudget < min.totalBudget,
      clampedDirection: targetBudget < min.totalBudget ? 'min' : null
    };
  }

  if (targetBudget >= max.totalBudget) {
    return {
      lo: max,
      hi: max,
      t: 0,
      isClamped: targetBudget > max.totalBudget,
      clampedDirection: targetBudget > max.totalBudget ? 'max' : null
    };
  }

  for (let i = 0; i < sortedLevels.length - 1; i += 1) {
    const lo = sortedLevels[i];
    const hi = sortedLevels[i + 1];
    if (targetBudget >= lo.totalBudget && targetBudget <= hi.totalBudget) {
      const span = hi.totalBudget - lo.totalBudget;
      return {
        lo,
        hi,
        t: span === 0 ? 0 : (targetBudget - lo.totalBudget) / span,
        isClamped: false,
        clampedDirection: null
      };
    }
  }

  // Unreachable given a sorted, non-empty list and the min/max guards above.
  return undefined;
};

const interpolateLinear = (lo: number, hi: number, t: number): number => lo + t * (hi - lo);

/** Interpolates an optional numeric field, treating a missing value on either side as 0. */
const interpolateOptional = (
  lo: number | undefined,
  hi: number | undefined,
  t: number
): number | undefined =>
  lo === undefined && hi === undefined ? undefined : interpolateLinear(lo ?? 0, hi ?? 0, t);

/**
 * Interpolates every channel-group budget between two channel-mix budget-map rows. Channel
 * budgets are non-negative by construction here: `t` is always in [0, 1] (see `findBudgetBracket`)
 * and both endpoints are non-negative spend figures, so no extra clamping is needed.
 */
export const interpolateChannelBudgets = (
  loPoint: ChannelMixDataPoint,
  hiPoint: ChannelMixDataPoint,
  t: number
): Record<string, number> => {
  const channelGroups = new Set(
    [...Object.keys(loPoint), ...Object.keys(hiPoint)].filter(key => key !== 'totalBudget')
  );
  const result: Record<string, number> = {};
  channelGroups.forEach(group => {
    const lo = Number(loPoint[group]) || 0;
    const hi = Number(hiPoint[group]) || 0;
    result[group] = interpolateLinear(lo, hi, t);
  });
  return result;
};

/** Relative tolerance used for the total-budget basis-consistency sanity check (see DEV-21410). */
export const BAU_BUDGET_CONSISTENCY_TOLERANCE_RATIO = 0.01;

/**
 * Sanity check (not a runtime throw): a blend of two sums is the blend of the totals, so summing
 * the interpolated channel budgets should reproduce the bracket target within a small tolerance.
 * A mismatch beyond tolerance usually means the channel-mix and marketing-outcomes budget levels
 * have drifted out of sync (e.g. hidden-channel filtering removed more than expected).
 */
export const isBauBudgetConsistent = (
  totalBudgetBau: number,
  targetBudget: number,
  toleranceRatio: number = BAU_BUDGET_CONSISTENCY_TOLERANCE_RATIO
): boolean => {
  if (targetBudget === 0) {
    return Math.abs(totalBudgetBau) < 1;
  }
  return Math.abs(totalBudgetBau - targetBudget) / Math.abs(targetBudget) <= toleranceRatio;
};

export interface InterpolatedBauResult {
  /** The interpolated BAU row, ready to merge alongside the EF's other budget-level points. */
  point: PinnedSpendPointData;
  /** Interpolated per-channel-group budgets, keyed by channel group name. */
  channelBudgets: Record<string, number>;
  /** Sum of `channelBudgets`; should equal `point.totalBudget` within tolerance (see 4c). */
  totalBudgetBau: number;
  /** The raw (pre-clamp) bracket target that was passed in. */
  requestedBudget: number;
  /** True if `requestedBudget` fell outside the modeled `[min, max]` range and was clamped. */
  isOutOfRange: boolean;
  clampedDirection: 'min' | 'max' | null;
  isBudgetConsistent: boolean;
}

/**
 * Simulates the BAU frontier point via linear interpolation between the two real optimized budget
 * levels bracketing `targetBudget` (see the DEV-21410 note above this section). Revenue is
 * interpolated per slot (matching how `marketingOutcomesData` carries per-model revenue),
 * then ROAS is recomputed from the interpolated revenue/budget -- never interpolated directly,
 * since revenue is concave in spend and a straight-line ROAS blend would compound that
 * (already-accepted, conservative) error on top of the revenue blend's.
 */
export const computeInterpolatedBau = (
  sortedMarketingOutcomes: PinnedSpendPointData[],
  channelMixByBudget: Map<number, ChannelMixDataPoint>,
  targetBudget: number
): InterpolatedBauResult | undefined => {
  const bracket = findBudgetBracket(sortedMarketingOutcomes, targetBudget);
  if (!bracket) {
    return undefined;
  }
  const { lo, hi, t, isClamped, clampedDirection } = bracket;

  const totalBudget = interpolateLinear(lo.totalBudget, hi.totalBudget, t);
  const revenueBySlot: Record<string, number> = {};
  new Set([...Object.keys(lo.revenueBySlot ?? {}), ...Object.keys(hi.revenueBySlot ?? {})]).forEach(
    slotId => {
      revenueBySlot[slotId] = interpolateLinear(
        lo.revenueBySlot?.[slotId] ?? 0,
        hi.revenueBySlot?.[slotId] ?? 0,
        t
      );
    }
  );
  const totalHolisticRevenue = interpolateOptional(
    lo.totalHolisticRevenue,
    hi.totalHolisticRevenue,
    t
  );

  const loChannelPoint = channelMixByBudget.get(lo.totalBudget);
  const hiChannelPoint = channelMixByBudget.get(hi.totalBudget);
  const channelBudgets =
    loChannelPoint && hiChannelPoint
      ? interpolateChannelBudgets(loChannelPoint, hiChannelPoint, t)
      : {};
  const totalBudgetBau = Object.values(channelBudgets).reduce((sum, value) => sum + value, 0);

  const roasDenominator = totalBudgetBau > 0 ? totalBudgetBau : totalBudget;
  const totalHolisticRoas =
    roasDenominator > 0 && totalHolisticRevenue !== undefined
      ? totalHolisticRevenue / roasDenominator
      : 0;

  return {
    point: {
      totalBudget,
      isBau: true,
      revenueBySlot,
      totalHolisticRevenue,
      totalHolisticRoas
    },
    channelBudgets,
    totalBudgetBau,
    requestedBudget: targetBudget,
    isOutOfRange: isClamped,
    clampedDirection,
    isBudgetConsistent: isBauBudgetConsistent(totalBudgetBau, totalBudget)
  };
};
